import React, {useEffect, useState} from 'react';
import {Platform, ScrollView, TouchableOpacity, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';

import LoadingIndicator from '/component/base/LoadingIndicator';
import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import ModalHeader from '/component/layout/ModalHeader';
import {useTranslation} from '/provider/TranslationProvider';
import {Apple} from '/theme/color/secondary.color';

import {Edge365CalendarScreen} from '../Edge365CalendarScreen/Edge365CalendarScreen';
import {styles} from '../Edge365ChallengeScreen/Edge365ChallengeScreen.style';
import {webScrollStyle} from '../Edge365ChallengeScreen/Edge365ChallengeScreen.style.web';
import StreakCard from '../Edge365ChallengeScreen/StreakCard';
import {EdgeDailyTracker} from '../EdgeDailyTracker';
import {DailyData} from '../EdgeDailyTracker/EdgeDailyTracker';

import {getTodayDetails} from './Edge365MainScreen.util';

export type EdgeChallengeDateParams = {
  month: number;
  year: number;
  coachId: string;
};

interface Edge365MainScreenProps {
  edge365ChallengeTasks: any[];
  hasEdge365ChallengeTasks: boolean;
  getEdge365ChallengeTasks: (params: EdgeChallengeDateParams) => void;
  latestStreakData: any;
  coachId: string;
  lastPaidRank: string;
  highestRankDescription: string;
  coachCurrentRank: string;
}

export const Edge365MainScreen = ({
  edge365ChallengeTasks,
  hasEdge365ChallengeTasks,
  getEdge365ChallengeTasks,
  latestStreakData,
  coachId: coachIdFromProps,
  lastPaidRank,
  highestRankDescription,
  coachCurrentRank,
}: Edge365MainScreenProps) => {
  const navigation = useNavigation();
  const {tt} = useTranslation('screen.EDGE365CHALLENGESCREEN');

  const todayStr = getTodayDetails().date;
  const [selectedDate, setSelectedDate] = useState<string>(todayStr);
  const [coachId, setCoachId] = useState(coachIdFromProps ?? '');

  const getDefaultDailyData = (date: string): DailyData => ({
    socialMediaPosts: 0,
    invitationsToExploreCoaching: 0,
    newFriends: 0,
    connections: 0,
    attendedWeeklyTraining: 0,
    personalHealthPlan: false,
    isChallengeCompleted: false,
    date,
  });
  const selectedData =
    edge365ChallengeTasks?.find(item => item.date === selectedDate) ||
    getDefaultDailyData(selectedDate);
  useEffect(() => {
    setCoachId(coachIdFromProps ?? '');
  }, [coachIdFromProps]);

  useEffect(() => {
    const year = getTodayDetails().year;
    const month = getTodayDetails().month;

    if (!hasEdge365ChallengeTasks) {
      getEdge365ChallengeTasks({coachId, month, year});
    }
  }, [hasEdge365ChallengeTasks, coachId]);

  if (!hasEdge365ChallengeTasks) {
    return <LoadingIndicator size="large" style={styles.loader} />;
  }
  const handleGoBack = () => navigation.goBack();
  return (
    <ModalHeader
      isProfileScreen
      onClose={handleGoBack}
      closeIconName="chevron-left"
      title={
        <TouchableOpacity
          onPress={handleGoBack}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Back">
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            style={{color: Apple}}
            isBold>
            {tt('back', 'Back')}
          </Text>
        </TouchableOpacity>
      }
      style={{base: styles.base}}>
      {Platform.OS === 'web' ? (
        <View style={webScrollStyle}>
          <Gutter>
            <StreakCard
              latestStreakData={latestStreakData}
              coachId={coachId}
              lastPaidRank={lastPaidRank}
              highestAchievedRank={highestRankDescription}
              coachCurrentRank={coachCurrentRank}
            />
            <Edge365CalendarScreen
              onDateSelect={date => setSelectedDate(date)}
              defaultDate={selectedDate}
              edge365ChallengeTasks={edge365ChallengeTasks}
              getEdge365ChallengeTasks={getEdge365ChallengeTasks}
              coachId={coachId}
              lastPaidRank={lastPaidRank}
              highestAchievedRank={highestRankDescription}
              coachCurrentRank={coachCurrentRank}
            />
            <EdgeDailyTracker
              onNextPress={() =>
                navigation.navigate('edge365-third-screen', {
                  completedDate: selectedDate,
                })
              }
              dailyData={selectedData}
              coachId={coachId}
            />
          </Gutter>
        </View>
      ) : (
        <ScrollView style={styles.content}>
          <Gutter>
            <StreakCard
              latestStreakData={latestStreakData}
              coachId={coachId}
              lastPaidRank={lastPaidRank}
              highestAchievedRank={highestRankDescription}
              coachCurrentRank={coachCurrentRank}
            />
            <Edge365CalendarScreen
              onDateSelect={date => setSelectedDate(date)}
              defaultDate={selectedDate}
              edge365ChallengeTasks={edge365ChallengeTasks}
              getEdge365ChallengeTasks={getEdge365ChallengeTasks}
              coachId={coachId}
              lastPaidRank={lastPaidRank}
              highestAchievedRank={highestRankDescription}
              coachCurrentRank={coachCurrentRank}
            />
            <EdgeDailyTracker
              onNextPress={() =>
                navigation.navigate('edge365-third-screen', {
                  completedDate: selectedDate,
                })
              }
              dailyData={selectedData}
              coachId={coachId}
            />
          </Gutter>
        </ScrollView>
      )}
    </ModalHeader>
  );
};

export default Edge365MainScreen;
